'use client';

import { useState, useEffect, useRef } from 'react';
import { logger } from '@/utils/logger';
import { Loader2 } from 'lucide-react';
import { getOptimizedImageUrl, getOptimalImageSize } from '@/lib/imageUtils';
import { useNonStandardAttributes } from '@/utils/reactDomUtils';

interface ImageWithFallbackProps {
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
  width?: number;
  height?: number;
  priority?: boolean;
  quality?: number;
  onLoad?: () => void;
  onError?: () => void;
  sizes?: string;
}

export function ImageWithFallback({
  src,
  alt,
  className = '',
  fallbackSrc = 'https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&q=80',
  width,
  height,
  priority = false,
  quality = 80,
  onLoad,
  onError,
  sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw',
}: ImageWithFallbackProps) {
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [srcSet, setSrcSet] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const imgRef = useRef<HTMLImageElement>(null);
  const isPictureSupported = typeof HTMLPictureElement !== 'undefined';

  useEffect(() => {
    if (!src) {
      setImgSrc(fallbackSrc);
      setIsLoading(false);
      return;
    }

    // Reset states when src changes
    setIsLoading(true);

    // Optimize URL if it's a Supabase URL
    const loadAndOptimizeImage = () => {
      try {
        // Get optimal sizes based on container or device width
        const containerWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
        const optimalWidth = width || getOptimalImageSize(containerWidth);
        
        // Get optimized URL for main image
        const optimizedUrl = getOptimizedImageUrl(src, {
          width: optimalWidth,
          height: height,
          quality: quality,
          format: 'webp', // Always use WebP for better compression
        });
        
        setImgSrc(optimizedUrl);
        
        // Generate srcset for responsive images if needed
        if (src.includes('supabase.co/storage') && isPictureSupported) {
          const breakpoints = [480, 768, 1200, 1600];
          const srcSetEntries = breakpoints.map(bpWidth => {
            const bpSrc = getOptimizedImageUrl(src, {
              width: bpWidth,
              height: height ? Math.round(height * (bpWidth / (width || bpWidth))) : undefined,
              quality: quality,
              format: 'webp',
            });
            return `${bpSrc} ${bpWidth}w`;
          });
          
          setSrcSet(srcSetEntries.join(', '));
        } else {
          setSrcSet(null);
        }
        
        // Preload the image if priority is true
        if (priority && typeof document !== 'undefined') {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'image';
          link.href = optimizedUrl;
          document.head.appendChild(link);
        }
      } catch (e) {
        logger.error('Error optimizing image URL:', e);
        setImgSrc(src);
        setSrcSet(null);
      }
    };
    
    loadAndOptimizeImage();
  }, [src, fallbackSrc, width, height, quality, priority]);

  // Use our custom hook to safely set non-standard attributes
  useNonStandardAttributes(imgRef, {
    fetchpriority: priority ? 'high' : 'auto',
    loading: priority ? 'eager' : 'lazy',
    decoding: 'async',
    crossorigin: 'anonymous',
  }, [priority, imgSrc]);

  const handleImageLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  const handleImageError = () => {
    setIsLoading(false);
    setImgSrc(fallbackSrc);
    setSrcSet(null);
    onError?.();
  };

  return (
    <div 
      className={`relative flex items-center justify-center ${className}`} 
      style={{ 
        width: width ? `${width}px` : '100%', 
        height: height ? `${height}px` : 'auto'
      }}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      )}
      
      {imgSrc && isPictureSupported && srcSet ? (
        <picture>
          <source 
            srcSet={srcSet} 
            sizes={sizes} 
            type="image/webp" 
          />
          <img
            ref={imgRef}
            src={imgSrc}
            alt={alt}
            className={`max-h-full max-w-full object-contain ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
            onLoad={handleImageLoad}
            onError={handleImageError}
            width={width}
            height={height}
            crossOrigin="anonymous"
          />
        </picture>
      ) : imgSrc && (
        <img
          ref={imgRef}
          src={imgSrc}
          alt={alt}
          className={`max-h-full max-w-full object-contain ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          onLoad={handleImageLoad}
          onError={handleImageError}
          loading={priority ? 'eager' : 'lazy'}
          width={width}
          height={height}
          decoding="async"
          crossOrigin="anonymous"
        />
      )}
    </div>
  );
} 